import type Phaser from 'phaser';
import type { Player } from '../types/game';
import { avatarAssetPath, avatarTextureKey } from './avatars';

/**
 * Player Identity System V1 — Phaser texture plumbing for in-game player-card
 * avatars. Mirrors the proven country-flag loader (countryFlags.ts) exactly:
 * on-demand loading of ONLY the avatars the current game needs (≤ 4), never
 * the whole 50-image library.
 *
 * Texture keys are deterministic (the catalog key itself, e.g. "avatar_7")
 * and only ever derived from allowlist-validated keys — server input can
 * never inject an arbitrary path or key. Textures live in the game-level
 * TextureManager, so they survive scene restarts (round transitions,
 * reconnect) and are never loaded twice.
 */

/** Max distinct avatars a single game can need (four seats). */
export const MAX_AVATARS_PER_GAME = 4;

// In-flight guard: keys queued on the loader but not yet resolved. Repeated
// game states arriving while a load is still in flight (WS bursts, quick
// reconnects) must not enqueue the same file twice. Keys are removed on the
// batch's 'complete' (fires whether individual files succeeded or failed)
// and eagerly on each file's 'loaderror', so a failed avatar can be retried
// by a later game state instead of being wedged as pending forever.
const pendingAvatarKeys = new Set<string>();

/** Test-only: reset the in-flight set between test cases. */
export function __resetPendingAvatarKeysForTests(): void {
  pendingAvatarKeys.clear();
}

/**
 * Queue the distinct, validated avatar textures needed by `players` (≤ 4)
 * and start the loader. Keys already in the texture cache OR already pending
 * on the loader are skipped, so round transitions, reconnects, and repeated
 * states during an in-flight load are all no-ops. `onLoaded` fires once
 * after a first-time load completes (never synchronously; not at all when
 * nothing needed loading) so the caller can redraw cards that were built
 * before the textures arrived. A file that fails to load never enters the
 * texture cache — the cards' textures.exists guard falls back to the
 * display-name initial, never a broken image.
 */
export function ensureAvatarTextures(
  scene: Phaser.Scene,
  players: readonly Player[],
  onLoaded?: () => void,
): number {
  const keys = new Set<string>();
  for (const p of players) {
    const key = avatarTextureKey(p.avatar_key);
    if (key) keys.add(key);
    if (keys.size >= MAX_AVATARS_PER_GAME) break;
  }

  const batchKeys: string[] = [];
  for (const key of keys) {
    if (scene.textures.exists(key)) {
      pendingAvatarKeys.delete(key); // self-heal any stale pending entry
      continue;
    }
    if (pendingAvatarKeys.has(key)) continue; // already in flight — never re-queue
    const path = avatarAssetPath(key);
    if (!path) continue;
    pendingAvatarKeys.add(key);
    batchKeys.push(key);
    scene.load.image(key, path);
  }

  if (batchKeys.length === 0) return 0;

  const onError = (file: { key?: string } | undefined) => {
    if (file?.key) pendingAvatarKeys.delete(file.key);
  };
  scene.load.on('loaderror', onError);
  scene.load.once('complete', () => {
    scene.load.off('loaderror', onError);
    for (const key of batchKeys) pendingAvatarKeys.delete(key);
    onLoaded?.();
  });
  if (!scene.load.isLoading()) scene.load.start();
  return batchKeys.length;
}
